/**
 * Vivo §4 data-layer seam for the HR portal — a dedicated axios instance (kept
 * separate from the legacy booking `HTTP`) that talks the raw §1 REST contract at
 * `/api/v1/*` and returns `res.data` directly. Every C-screen's api module calls
 * through here.
 *
 *  - Mock mode (Phase 2.5, VUE_APP_USE_MOCKS=true): a custom adapter answers from
 *    the in-repo mock router — no network.
 *  - Real mode (Phase 3+, integration): drop the flag + point VUE_APP_API_URL at
 *    the NestJS origin (empty ⇒ same-origin `/api/v1` via the nginx proxy); the
 *    same calls hit the real backend.
 *
 * Auth (A2): the access token lives in memory (token-store); every request gets a
 * fresh `Authorization: Bearer` + `Accept-Language`; a 401 on a protected call is
 * silently refreshed ONCE through the httpOnly refresh cookie, then replayed. All
 * of that is the node-tested `installAuthInterceptors` seam.
 */
import axios, { AxiosInstance } from "axios";

import { installAuthInterceptors } from "./auth-interceptors";
import { sessionFromToken } from "./jwt";
import * as tokenStore from "./token-store";
import { clearSessionKeys } from "./logout";

const apiOrigin = process.env.VUE_APP_API_URL || "";

export const vivoHttp: AxiosInstance = axios.create({
  baseURL: apiOrigin + "/api/v1",
  // Send the httpOnly refresh cookie on every call (needed for /auth/refresh).
  // Same-origin via the nginx proxy in the integration env; harmless otherwise.
  withCredentials: true,
});

function currentLang(): string {
  const lang =
    typeof localStorage !== "undefined" ? localStorage.getItem("lang") : null;
  return lang || "ar-EG";
}

// Silent refresh: POST /auth/refresh with no body — the backend reads the
// httpOnly refresh cookie (withCredentials) and rotates it, returning a fresh
// access token which we store in memory. Returns false to let the 401 propagate.
async function refreshAccessToken(): Promise<boolean> {
  try {
    const res = await vivoHttp.post("/auth/refresh", undefined, {
      withCredentials: true,
    });
    const token = res && res.data && res.data.accessToken;
    if (!token) {
      return false;
    }
    tokenStore.setAccessToken(token);
    try {
      tokenStore.setSession(sessionFromToken(token));
    } catch (e) {
      // A malformed token shouldn't wedge the retry; the bearer is still set.
    }
    return true;
  } catch (e) {
    return false;
  }
}

// Refresh gave up (cookie expired/revoked): tear the session down and bounce to
// the login route (history mode). Shares clearSessionKeys with the sign-out
// buttons (api/vivo/logout.js) so an expired session and a deliberate sign-out
// can never clear different things. No /auth/logout call here: the refresh
// family is already dead, which is why we are in this function.
function onAuthLost(): void {
  tokenStore.clearAuth();
  try {
    if (typeof localStorage !== "undefined") {
      clearSessionKeys(localStorage);
    }
  } catch (e) {
    /* ignore */
  }
  try {
    if (typeof window !== "undefined" && window.location) {
      window.location.assign("/");
    }
  } catch (e) {
    /* ignore */
  }
}

installAuthInterceptors(vivoHttp, {
  getToken: () => tokenStore.getAccessToken(),
  getLang: currentLang,
  onRefresh: refreshAccessToken,
  onAuthLost: onAuthLost,
});

// Required lazily INSIDE the flag check so webpack's constant folding drops the
// whole mock layer (dataset, handlers) from non-mock builds like integration.
if (process.env.VUE_APP_USE_MOCKS === "true") {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { createMockAdapter } = (require as any)("../../mocks/adapter");
  vivoHttp.defaults.adapter = createMockAdapter();
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, unknown>,
): Promise<T> {
  const res = await vivoHttp.get<T>(path, { params });
  return res.data;
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await vivoHttp.post<T>(path, body);
  return res.data;
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const res = await vivoHttp.patch<T>(path, body);
  return res.data;
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const res = await vivoHttp.put<T>(path, body);
  return res.data;
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await vivoHttp.delete<T>(path);
  return res.data;
}

/** Authenticated binary download (e.g. claim documents) — the bearer header is
 *  attached by the shared interceptors, so no unauthenticated/raw file URL is used. */
export async function apiGetBlob(path: string): Promise<Blob> {
  const res = await vivoHttp.get<Blob>(path, { responseType: "blob" });
  return res.data;
}

/** Multipart upload (e.g. POST /claims/:id/documents). The browser sets the
 *  multipart boundary itself, so no Content-Type is forced here. */
export async function apiPostForm<T>(path: string, form: FormData): Promise<T> {
  const res = await vivoHttp.post<T>(path, form, {
    headers: { "Content-Type": undefined as unknown as string },
  });
  return res.data;
}
